import React from 'react';
import { matchPath, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/AuthProvider';
import routes from './adminRoutes';

const ADMIN_PATH = '/admin';

const warningNotification = (message) => [
    {
        type: 'warning',
        message,
        autoClose: 8000,
    },
];

const hasPermissionName = (sessionUser, permissionName) =>
    sessionUser?.role?.permissions?.some((permission) => permission.name === permissionName);

export const getSafeAdminPath = (sessionUser) => {
    if (
        sessionUser?.role?.code === 'patient' ||
        hasPermissionName(sessionUser, 'isPatient')
    ) {
        return '/admin/homework/list';
    }

    if (
        !hasPermissionName(sessionUser, 'canHavePatients') &&
        hasPermissionName(sessionUser, 'manageAllPatients')
    ) {
        return '/admin/patients/all';
    }

    if (hasPermissionName(sessionUser, 'canHavePatients')) {
        return '/admin/sessions/today';
    }

    return '/admin/settings';
};

const routePathFromLocation = (pathname) => {
    const relativePath = pathname.startsWith(ADMIN_PATH)
        ? pathname.slice(ADMIN_PATH.length)
        : pathname;

    return relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
};

const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, sessionUser } = useAuth();
    const location = useLocation();

    if (!isAuthenticated || !sessionUser || Object.keys(sessionUser).length === 0) {
        return (
            <Navigate
                to="/"
                replace
                state={{
                    notifications: warningNotification(
                        'You have tried to access a page which requires you to log in.'
                    ),
                }}
            />
        );
    }

    const relativePath = routePathFromLocation(location.pathname);
    const matchedRoute = routes.find(({ path }) =>
        matchPath({ path, end: true }, relativePath)
    );

    if (!matchedRoute) {
        return (
            <Navigate
                to={getSafeAdminPath(sessionUser)}
                replace
                state={{
                    notifications: warningNotification(
                        "You have tried to access a page which you don't have permission to access."
                    ),
                }}
            />
        );
    }

    const { permissions } = matchedRoute;
    const organizationIsActive = sessionUser.organization?.subscriptionData?.isActive;

    if (!organizationIsActive && matchedRoute.name !== 'Settings') {
        return (
            <Navigate
                to="/admin/settings"
                replace
                state={{
                    notifications: warningNotification(
                        'You have tried to access a page which requires you to have an active account. Please check your subscription details.'
                    ),
                }}
            />
        );
    }

    const hasPermission =
        !permissions ||
        permissions.length === 0 ||
        permissions.some((routePermission) => hasPermissionName(sessionUser, routePermission));

    if (!hasPermission) {
        return (
            <Navigate
                to={getSafeAdminPath(sessionUser)}
                replace
                state={{
                    notifications: warningNotification(
                        "You have tried to access a page which you don't have permission to access."
                    ),
                }}
            />
        );
    }

    return children;
};

export default ProtectedRoute;
